/**
 * Контакты поддержки, заданные на нашей стороне.
 *
 * Блок поддержки адресный, а не общий: по умолчанию его не видит никто. Школам из этой
 * карты показывается список мессенджеров, участникам «Астана дарыны» — номер из
 * school.phone со ссылкой в WhatsApp (isSchoolPhoneSupportVisible). Каналы у этих школ
 * есть, а полей под них в API пока нет — та же временная мера, что и
 * QUIZ_TOOLS_FAB_SCHOOL_IDS.
 *
 * Карта разложена по брендам: id школы приходит из базы конкретного бэкенда и за его
 * пределами означает другую школу. Ключ «бренд + id» делает промах в чужого арендатора
 * невозможным, а не забытой проверкой.
 *
 * Ссылки и названия каналов собирает lib/supportChannels — тот же модуль, что у формы
 * регистрации, поэтому канал достаточно вписать сюда источником: номером или ником.
 *
 * Кабинеты trustexam заводят каналы сами: менеджер вписывает их в профиле, они ложатся
 * строками school_contacts (readSchoolSupportContacts), и getSchoolSupportContacts
 * предпочитает их карте. Карта остаётся запасной, пока её школы не перенесут каналы.
 */

import { getBrandKeyFromEnv, isSchoolSupportContactsEnabled } from "@/lib/branding";
import { KAZAKHTELECOM_SCHOOL_ID } from "@/lib/registrationFormFields";
import {
  buildSupportChannelUrl,
  getVisibleSupportChannels,
  type SupportChannelKey,
  type SupportChannelSources,
} from "@/lib/supportChannels";

/** Haileybury: и менеджеры школы, и студенты, заходящие под ней. Школа бренда trustexam. */
export const HAILEYBURY_SCHOOL_ID = 528;

const SUPPORT_SOURCES: Record<string, Record<number, SupportChannelSources>> = {
  trustexam: {
    /* Номера нет намеренно: без источника WhatsApp отпадает сам, у школы остаётся бот. */
    [HAILEYBURY_SCHOOL_ID]: {
      usernames: { telegram: "Haileybury_bot" },
    },
    /* Закрытый чат поддержки участников, тот же, что в инструкции к техническому туру. */
    [KAZAKHTELECOM_SCHOOL_ID]: {
      usernames: { telegram: "https://t.me/+_B8xWtLkn2w0NjFi" },
    },
  },
};

export type SupportContact = {
  /** "phone" — номер для звонка, без мессенджера. */
  key: SupportChannelKey | "phone";
  /** Название мессенджера или сам номер — одинаковы во всех локалях, намеренно не ключ i18n. */
  name: string;
  url: string;
};

/* Каналы в порядке SUPPORT_CHANNEL_CONFIGS; канал без источника отпадает сам. */
function toSupportContacts(sources: SupportChannelSources): SupportContact[] {
  return getVisibleSupportChannels().flatMap((channel) => {
    const url = buildSupportChannelUrl(channel.key, sources);
    return url ? [{ key: channel.key, name: channel.name, url }] : [];
  });
}

/** Каналы школы из карты выше. */
export function getSupportContacts(
  schoolId: number | null | undefined,
  brandKey: string = getBrandKeyFromEnv(),
): SupportContact[] {
  const sources =
    typeof schoolId === "number" ? SUPPORT_SOURCES[brandKey]?.[schoolId] : undefined;

  return sources ? toSupportContacts(sources) : [];
}

/**
 * Строка school_contacts, как её отдаёт GET /school.json (contacts:type,value,name).
 * Тот же список читал SupportContactsComponent старого фронта: whatsapp, telegram,
 * max, email — значением номер, ник или ссылка.
 */
export type SchoolContactRow = {
  type?: string | null;
  value?: string | null;
  name?: string | null;
};

/**
 * Контакты, которые менеджер вписал в профиле, строками school_contacts:
 *
 *   [{ type: "phone", value: "+77771234567" },
 *    { type: "whatsapp", value: "+77771234567" },
 *    { type: "telegram", value: "@aidos_serikuly" }]
 *
 * WhatsApp — признак номера, а не своё поле: номер поддержки бывает и без мессенджера,
 * поэтому строка whatsapp повторяет номер, только когда тумблер включён. Telegram
 * принимает то же, что форма регистрации: ник, номер или ссылку t.me. MAX кабинетам
 * trustexam не нужен и здесь не заводится.
 */
export type SchoolSupportContacts = {
  phone: string;
  whatsapp: boolean;
  telegram: string;
};

/* Типы строк, которыми владеет блок в профиле; остальные он переносит как есть. */
const OWN_CONTACT_TYPES = ["phone", "whatsapp", "telegram"];

const rowValue = (rows: readonly SchoolContactRow[], type: string) =>
  rows.find((row) => row.type?.toLowerCase() === type)?.value?.trim() ?? "";

/** Контакты из строк школы; незаполненное поле приходит пустой строкой. */
export function readSchoolSupportContacts(
  rows: readonly SchoolContactRow[] | null | undefined,
): SchoolSupportContacts {
  const list = rows ?? [];
  const whatsapp = rowValue(list, "whatsapp");

  /* Строку whatsapp без phone мог завести не наш блок — номер тогда берём из неё. */
  return {
    phone: rowValue(list, "phone") || whatsapp,
    whatsapp: whatsapp !== "",
    telegram: rowValue(list, "telegram"),
  };
}

/**
 * Строки для PUT /schools/{id}: свои типы заменяются, чужие остаются.
 *
 * Бэкенд пересоздаёт school_contacts из переданного списка целиком, поэтому строку,
 * которую завёл не этот блок (почту, MAX), обязательно отправить обратно.
 */
export function withSchoolSupportContacts(
  rows: readonly SchoolContactRow[] | null | undefined,
  contacts: SchoolSupportContacts,
): SchoolContactRow[] {
  const phone = contacts.phone.trim();
  const own: SchoolContactRow[] = [
    { type: "phone", value: phone },
    { type: "whatsapp", value: contacts.whatsapp ? phone : "" },
    { type: "telegram", value: contacts.telegram.trim() },
  ];

  return (rows ?? [])
    .filter((row) => !OWN_CONTACT_TYPES.includes(row.type?.toLowerCase() ?? ""))
    .map(({ type, value, name }): SchoolContactRow => ({ type, value, name }))
    .concat(own.filter((row) => row.value));
}

/** Каналы школы участника: из её school_contacts, а если там пусто — из карты выше. */
export function getSchoolSupportContacts(
  school: { id?: number | null; contacts?: readonly SchoolContactRow[] | null } | null | undefined,
  brandKey: string = getBrandKeyFromEnv(),
): SupportContact[] {
  if (isSchoolSupportContactsEnabled(brandKey)) {
    const own = readSchoolSupportContacts(school?.contacts);
    /* Тот же порог, что у wa.me в buildSupportChannelUrl: короче — не номер. */
    const digits = own.phone.replace(/\D/g, "");
    const phone: SupportContact[] =
      digits.length >= 8
        ? [{ key: "phone", name: formatSupportPhone(own.phone), url: `tel:+${digits}` }]
        : [];
    /* Без тумблера номер остаётся номером: WhatsApp получает его только по признаку. */
    const contacts = phone.concat(
      toSupportContacts({
        phone: own.whatsapp ? own.phone : null,
        usernames: { telegram: own.telegram },
      }),
    );
    if (contacts.length > 0) return contacts;
  }

  return getSupportContacts(school?.id, brandKey);
}

/**
 * Номер из school.phone показывается только участникам «Астана дарыны».
 *
 * Признак тот же, что у useDarynClassFilter: ключи astana_daryn_* в users.additional,
 * заполненные формой регистрации. По school_id не гейтим — id тенанта различаются
 * между бэкендами, на старом фронте их держали списком.
 */
export function isSchoolPhoneSupportVisible(
  additional: Record<string, unknown> | null | undefined,
): boolean {
  return Object.keys(additional ?? {}).some((key) =>
    key.startsWith("astana_daryn_"),
  );
}

/**
 * Второй номер поддержки «Астана дарыны»: в school.phone помещается только один,
 * а школа просила показывать оба. Временная мера, как и карта каналов выше.
 */
const ASTANA_DARYN_EXTRA_PHONE = "+7 771 423 18 08";

/* Разбивка в school.phone своя («+7776 388 3388»), а печатаются номера одинаково. */
function formatSupportPhone(phone: string): string {
  const groups = phone.replace(/\D/g, "").match(/^7(\d{3})(\d{3})(\d{2})(\d{2})$/);
  return groups ? `+7 ${groups.slice(1).join(" ")}` : phone;
}

/**
 * Номера «Астана дарыны» в одном оформлении: WhatsApp заведён на обоих.
 *
 * Школьный идёт первым — он остаётся основным, наш добавлен к нему.
 */
export function getSchoolPhoneSupportNumbers(
  phone: string | null | undefined,
): string[] {
  const own = phone?.trim();
  if (!own) return [];

  return [own, ASTANA_DARYN_EXTRA_PHONE].map(formatSupportPhone);
}
